import { CATEGORIES, BLOCKS, CAT_COLOR } from './catalog.js';
import { compileProject, highlight, parse } from './language.js';
import {
  addVariable,
  allLists,
  allVariables,
  defaultProject,
  loadSb3,
  makeSprite,
  paintedCostume,
  saveSb3,
  surpriseCostume,
  uniqueName,
} from './sb3.js';
import { emptyVectorCostume } from './assets.js';
import { Player } from './player.js';
import { rasterizeSvg, fitSvgToArtboard, fitBitmapToArtboard } from './svg-quality.js';
import { PaintEditor } from './paint.js';
import { SoundEditor, newPopSound } from './sounded.js';
import { ICONS } from './icons.js';
import { Completer } from './complete.js';
import { deleteSave, listSaves, newSaveId, readSave, writeSave } from './local-saves.js';

class App {
  constructor() {
    this.project = defaultProject();
    this.current = this.project.targets[1];
    this.player = new Player(document.getElementById('stage'));
    this.paint = new PaintEditor(this);
    this.sounds = new SoundEditor(this);
    this.category = 'motion';
    this.tab = 'code';
    this.shownVars = new Set();
    this.installIcons();
    this.bindChrome();
    this.completer = new Completer(this);
    this.renderAll();
    this.player.load(this.project);
    this.player.onAsk = (ask) => {
      const bar = document.getElementById('ask-bar');
      if (!ask) { bar.classList.add('hidden'); return; }
      bar.classList.remove('hidden');
      document.getElementById('ask-question').textContent = ask.question;
      const input = document.getElementById('ask-input');
      input.value = '';
      input.focus();
      input.onkeydown = (e) => {
        if (e.key === 'Enter') this.player.submitAnswer(input.value);
      };
    };
    this.player.onMonitors = (mons) => this.renderMonitors(mons);
  }

  installIcons() {
    const set = (id, html) => { const el = document.getElementById(id); if (el) el.innerHTML = html; };
    set('tab-code-icon', ICONS.code);
    set('tab-costumes-icon', ICONS.costumes);
    set('tab-sounds-icon', ICONS.sounds);
    set('stage-flag', ICONS.play.replace('currentColor', '#39d267').replace('<path', '<path fill="#39d267" stroke="#1b8f3a"'));
    document.getElementById('stage-flag').innerHTML = `<svg viewBox="0 0 24 24" width="22" height="22"><path d="M4 3v18M4 4h12l-2.2 4L16 12H4" fill="#39d267" stroke="#1b8f3a" stroke-width="1.2"/></svg>`;
    document.getElementById('stage-stop').innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20"><rect x="5" y="5" width="14" height="14" rx="2" fill="#ec5959"/></svg>`;
    document.getElementById('stage-recompile').innerHTML = ICONS.recompile;
    document.getElementById('stage-full').innerHTML = ICONS.fullscreen;

    const addBtns = (hostId, items) => {
      const host = document.getElementById(hostId);
      if (!host) return;
      host.innerHTML = '';
      for (const it of items) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'icon-btn';
        b.dataset.tip = it.title;
        b.setAttribute('aria-label', it.title);
        b.innerHTML = ICONS[it.icon];
        b.onclick = it.on;
        host.appendChild(b);
      }
    };
    addBtns('sprite-add-row', [
      { title: 'Paint', icon: 'paint', on: () => this.addSprite(false) },
      { title: 'Upload', icon: 'upload', on: () => document.getElementById('file-costume').click() },
      { title: 'Surprise', icon: 'surprise', on: () => this.addSprite(true) },
    ]);
    addBtns('costume-add-row', [
      { title: 'Paint', icon: 'paint', on: () => this.addCostume(paintedCostume(this.current?.isStage ? 'backdrop' : 'costume', emptyVectorCostume(480, 360))) },
      { title: 'Upload', icon: 'upload', on: () => document.getElementById('file-costume').click() },
      { title: 'Surprise', icon: 'surprise', on: () => this.addCostume(surpriseCostume()) },
    ]);
    addBtns('sound-add-row', [
      { title: 'Record', icon: 'record', on: () => {
        this.current.sounds.push(newPopSound());
        this.current._soundIndex = this.current.sounds.length - 1;
        this.renderSoundList();
        this.sounds.loadSound();
        this.sounds.record();
      } },
      { title: 'Upload', icon: 'upload', on: () => document.getElementById('file-sound').click() },
      { title: 'Surprise', icon: 'surprise', on: () => {
        this.current.sounds.push(newPopSound());
        this.current._soundIndex = this.current.sounds.length - 1;
        this.renderSoundList();
        this.sounds.loadSound();
      } },
    ]);
  }

  bindChrome() {
    document.querySelectorAll('.menu-item').forEach((item) => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        document.querySelectorAll('.menu-item').forEach((x) => x.classList.toggle('open', x === item && !item.classList.contains('open')));
      });
    });
    window.addEventListener('click', () => document.querySelectorAll('.menu-item').forEach((x) => x.classList.remove('open')));

    document.querySelector('[data-action="new"]').onclick = () => this.newProject();
    document.querySelector('[data-action="open"]').onclick = () => document.getElementById('file-sb3').click();
    document.querySelector('[data-action="save"]').onclick = () => this.save();
    document.querySelector('[data-action="save-browser"]').onclick = () => this.saveToBrowser(false);
    document.querySelector('[data-action="save-browser-as"]').onclick = () => this.saveToBrowser(true);
    document.querySelector('[data-action="saved-projects"]').onclick = () => this.showSavedProjects();
    document.querySelector('[data-action="export-txt"]').onclick = () => this.exportTxt();
    document.getElementById('saves-close').onclick = () => this.hideSavedProjects();
    document.getElementById('saves-modal').onclick = (e) => {
      if (e.target.id === 'saves-modal') this.hideSavedProjects();
    };
    document.querySelector('[data-action="undo"]').onclick = () => document.execCommand('undo');
    document.querySelector('[data-action="redo"]').onclick = () => document.execCommand('redo');
    document.querySelector('[data-action="turbo"]').onclick = () => this.toggleTurbo();

    document.getElementById('file-sb3').onchange = (e) => this.openFile(e.target.files[0]);
    document.getElementById('file-costume').onchange = (e) => this.uploadCostume(e.target.files[0]);
    document.getElementById('file-sound').onchange = (e) => this.uploadSound(e.target.files[0]);

    document.getElementById('stage-flag').onclick = () => this.run();
    document.getElementById('stage-stop').onclick = () => this.player.stop();
    document.getElementById('stage-recompile').onclick = () => this.recompile();
    document.getElementById('turbo-check').onchange = (e) => {
      this.player.turbo = e.target.checked;
    };
    document.getElementById('stage-full').onclick = () => {
      document.querySelector('.stage-col').classList.toggle('fullscreen');
    };
    document.getElementById('project-name').onchange = (e) => { this.project._name = e.target.value; };

    document.querySelectorAll('.tab').forEach((tab) => {
      tab.onclick = () => this.setTab(tab.dataset.tab);
    });

    const code = document.getElementById('code-input');
    code.addEventListener('input', () => this.onCode());
    code.addEventListener('scroll', () => this.syncScroll());
    code.addEventListener('keydown', (e) => this.onCodeKey(e));
    document.getElementById('code-scroll')?.addEventListener('scroll', () => this.syncScroll());

    document.getElementById('stage-target').onclick = (e) => {
      if (e.target.closest('#open-backdrops')) {
        this.openBackdrops();
        return;
      }
      this.selectTarget(this.project.targets[0]);
    };
    document.getElementById('open-backdrops').onclick = (e) => {
      e.stopPropagation();
      this.openBackdrops();
    };

    document.getElementById('sprite-name').onchange = (e) => { this.current.name = e.target.value; this.renderSprites(); };
    ['x', 'y', 'size', 'dir'].forEach((k) => {
      const map = { x: 'x', y: 'y', size: 'size', dir: 'direction' };
      document.getElementById('sprite-' + k).onchange = (e) => {
        this.current[map[k]] = Number(e.target.value);
        this.player.draw();
      };
    });
    document.getElementById('sprite-show').onchange = (e) => { this.current.visible = e.target.checked; this.player.draw(); };
    document.getElementById('sprite-rot').onchange = (e) => { this.current.rotationStyle = e.target.value; this.player.draw(); };

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.hideSavedProjects();
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        this.save();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        this.run();
      }
    });
  }

  renderAll() {
    document.getElementById('project-name').value = this.project._name || 'Untitled';
    this.updateCostumeTabLabel();
    this.renderCategories();
    this.renderBlocks();
    this.renderCode();
    this.renderSprites();
    this.renderSpriteInfo();
    this.renderCostumeList();
    this.renderSoundList();
    document.getElementById('backdrop-count').textContent = this.project.targets[0].costumes.length;
  }

  renderCategories() {
    const el = document.getElementById('category-list');
    el.innerHTML = '';
    const cats = this.current?.isStage ? CATEGORIES.filter((c) => c.id !== 'motion') : CATEGORIES;
    if (this.current?.isStage && this.category === 'motion') this.category = 'looks';
    for (const c of cats) {
      const b = document.createElement('button');
      b.className = 'cat-btn' + (c.id === this.category ? ' active' : '');
      const dot = document.createElement('span');
      dot.className = 'dot';
      dot.style.background = c.color;
      const name = document.createElement('span');
      name.textContent = c.name;
      b.append(dot, name);
      b.onclick = () => { this.category = c.id; this.renderCategories(); this.renderBlocks(); };
      el.appendChild(b);
    }
  }

  renderBlocks() {
    const el = document.getElementById('block-list');
    el.innerHTML = '';
    if (this.category === 'variables') {
      const tools = document.createElement('div');
      tools.className = 'var-tools';
      const mk = (label, list) => {
        const b = document.createElement('button');
        b.textContent = label;
        b.onclick = () => this.makeVar(list);
        return b;
      };
      tools.append(mk('Make a Variable', false), mk('Make a List', true));
      el.appendChild(tools);
      const vars = allVariables(this.project, this.current);
      const lists = allLists(this.project, this.current);
      if (!vars.length && !lists.length) {
        const hint = document.createElement('div');
        hint.className = 'palette-hint';
        hint.textContent = 'Variables are created here, then used as names in the script: set(score, 0) or score = 0';
        el.appendChild(hint);
      }
      for (const v of vars) {
        const row = document.createElement('div');
        row.className = 'var-item';
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.checked = this.shownVars.has(v.name);
        cb.onchange = () => {
          if (cb.checked) this.shownVars.add(v.name);
          else this.shownVars.delete(v.name);
          this.player.showMon(v.name, cb.checked, false);
          this.player.draw();
          this.renderMonitors(this.player.collectMonitors());
        };
        const btn = this.blockButton({ fn: v.name, cat: 'variables', kind: 'reporter', snippet: v.name });
        row.append(cb, btn);
        el.appendChild(row);
      }
      for (const v of lists) {
        const btn = this.blockButton({ fn: v.name, cat: 'variables', kind: 'reporter', snippet: v.name });
        el.appendChild(btn);
      }
    }
    const blocks = BLOCKS.filter((b) => b.cat === this.category && !(this.category === 'variables' && !allVariables(this.project, this.current).length && ['set', 'change', 'show_variable', 'hide_variable'].includes(b.fn)));
    if (this.category === 'events') {
      const hint = document.createElement('div');
      hint.className = 'palette-hint';
      hint.textContent = 'Every script chain starts with when...():';
      el.appendChild(hint);
    }
    if (this.category === 'operators') {
      const hint = document.createElement('div');
      hint.className = 'palette-hint';
      hint.textContent = 'Use + - * / % > < == and or not. Join text with join(a, b), not +.';
      el.appendChild(hint);
    }
    for (const b of BLOCKS.filter((x) => x.cat === this.category)) {
      if (this.category === 'variables' && !['set', 'change', 'show_variable', 'hide_variable', 'add_to', 'delete_of', 'delete_all_of', 'insert_at', 'replace_item', 'item_of', 'item_num_of', 'length_of_list', 'list_contains', 'show_list', 'hide_list'].includes(b.fn)) continue;
      el.appendChild(this.blockButton(b));
    }
    if (this.category === 'myblocks') {
      const btn = document.createElement('button');
      btn.className = 'palette-block';
      btn.style.background = CAT_COLOR.myblocks;
      btn.textContent = 'Make a Block';
      btn.onclick = () => this.makeBlock();
      el.prepend(btn);
    }
  }

  blockButton(b) {
    const btn = document.createElement('button');
    btn.className = 'palette-block ' + (b.kind || '');
    btn.style.background = CAT_COLOR[b.cat] || '#888';
    btn.textContent = b.snippet || `${b.fn}()`;
    btn.onclick = () => this.insert(b.snippet || `${b.fn}()`);
    return btn;
  }

  insert(text) {
    const ta = document.getElementById('code-input');
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const v = ta.value;
    const lineStart = v.lastIndexOf('\n', start - 1) + 1;
    const indent = v.slice(lineStart, start).match(/^ */)[0];
    let snippet = text;
    if (snippet.endsWith(':')) snippet += '\n' + indent + '    ';
    else if (!snippet.endsWith('\n')) snippet += '\n' + indent;
    ta.value = v.slice(0, start) + snippet + v.slice(end);
    const pos = start + snippet.length;
    ta.selectionStart = ta.selectionEnd = pos;
    ta.focus();
    this.onCode();
  }

  onCode() {
    this.current.scriptText = document.getElementById('code-input').value;
    this.renderHighlight();
    this.validate();
    this.completer?.onInput();
  }

  onCodeKey(e) {
    if (this.completer?.onKey(e)) return;
    const ta = e.target;
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) this.unindentLine(ta);
      else {
        const s = ta.selectionStart;
        ta.value = ta.value.slice(0, s) + '    ' + ta.value.slice(ta.selectionEnd);
        ta.selectionStart = ta.selectionEnd = s + 4;
      }
      this.onCode();
      return;
    }
    if ((e.key === 'Backspace' || e.key === 'Delete') && ta.selectionStart === ta.selectionEnd) {
      const s = ta.selectionStart;
      if (e.key === 'Backspace' && s >= 4) {
        const lineStart = ta.value.lastIndexOf('\n', s - 1) + 1;
        const before = ta.value.slice(lineStart, s);
        if (/^ +$/.test(before) && before.length % 4 === 0) {
          e.preventDefault();
          ta.value = ta.value.slice(0, s - 4) + ta.value.slice(s);
          ta.selectionStart = ta.selectionEnd = s - 4;
          this.onCode();
          return;
        }
      }
    }
    if (e.key === 'Enter') {
      const s = ta.selectionStart;
      const lineStart = ta.value.lastIndexOf('\n', s - 1) + 1;
      const line = ta.value.slice(lineStart, s);
      const indent = line.match(/^ */)[0];
      const extra = line.trim().endsWith(':') ? '    ' : '';
      e.preventDefault();
      ta.value = ta.value.slice(0, s) + '\n' + indent + extra + ta.value.slice(ta.selectionEnd);
      ta.selectionStart = ta.selectionEnd = s + 1 + indent.length + extra.length;
      this.onCode();
    }
  }

  unindentLine(ta) {
    const s = ta.selectionStart;
    const lineStart = ta.value.lastIndexOf('\n', s - 1) + 1;
    const lineEnd = ta.value.indexOf('\n', s);
    const end = lineEnd < 0 ? ta.value.length : lineEnd;
    const line = ta.value.slice(lineStart, end);
    const n = Math.min(4, line.match(/^ */)[0].length);
    if (!n) return;
    ta.value = ta.value.slice(0, lineStart) + line.slice(n) + ta.value.slice(end);
    ta.selectionStart = ta.selectionEnd = Math.max(lineStart, s - n);
  }

  renderCode() {
    const ta = document.getElementById('code-input');
    ta.value = this.current.scriptText || '';
    document.getElementById('sprite-script-label').textContent = this.current.name;
    this.renderHighlight();
    this.validate();
    this.completer?.hide();
  }

  renderHighlight() {
    const ta = document.getElementById('code-input');
    const hl = document.getElementById('code-highlight');
    const wrap = ta.parentElement;
    const text = ta.value;
    try {
      hl.innerHTML = highlight(text) + '\n';
    } catch {
      hl.textContent = text;
    }
    const lines = Math.max(1, text.split('\n').length);
    document.getElementById('line-numbers').textContent = Array.from({ length: lines }, (_, i) => i + 1).join('\n');
    const h = Math.max(wrap.clientHeight, lines * 20 + 40);
    ta.style.height = h + 'px';
    hl.style.height = h + 'px';
    this.syncScroll();
    this.syncEditorOverlay();
    requestAnimationFrame(() => this.syncEditorOverlay());
  }

  syncEditorOverlay() {
    const ta = document.getElementById('code-input');
    const wrap = ta.parentElement;
    if (!wrap) return;
    const bg = getComputedStyle(ta).backgroundColor;
    wrap.classList.toggle('plain-code', isOpaqueColor(bg));
  }

  syncScroll() {
    const ta = document.getElementById('code-input');
    const wrap = ta.parentElement;
    const hl = document.getElementById('code-highlight');
    hl.style.transform = `translate(${-ta.scrollLeft}px, ${-ta.scrollTop}px)`;
    document.getElementById('line-numbers').scrollTop = wrap?.scrollTop || ta.scrollTop;
  }

  validate() {
    const status = document.getElementById('parse-status');
    try {
      parse(this.current.scriptText || '');
      if (!this.player.running) compileProject(this.project);
      status.textContent = 'Ready';
      status.className = 'parse-ok';
    } catch (e) {
      status.textContent = e.message;
      status.className = 'parse-err';
    }
  }

  renderSprites() {
    const el = document.getElementById('sprite-list');
    el.innerHTML = '';
    for (const t of this.project.targets.filter((s) => !s.isStage)) {
      const card = document.createElement('div');
      card.className = 'sprite-card' + (t === this.current ? ' selected' : '');
      const cv = document.createElement('canvas');
      cv.width = 80; cv.height = 72;
      this.drawThumb(cv, t.costumes[t.currentCostume]);
      const nm = document.createElement('div');
      nm.className = 'nm';
      nm.textContent = t.name;
      card.append(cv, nm);
      card.onclick = () => this.selectTarget(t);
      card.oncontextmenu = (e) => {
        e.preventDefault();
        if (confirm(`Delete ${t.name}?`)) this.deleteSprite(t);
      };
      el.appendChild(card);
    }
    document.getElementById('stage-target').classList.toggle('selected', this.current.isStage);
    this.drawThumb(document.getElementById('stage-thumb'), this.project.targets[0].costumes[this.project.targets[0].currentCostume]);
    document.getElementById('backdrop-count').textContent = this.project.targets[0].costumes.length;
  }

  async drawThumb(canvas, costume) {
    const g = canvas.getContext('2d');
    g.clearRect(0, 0, canvas.width, canvas.height);
    if (!costume) return;
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    const fit = (w, h, src) => {
      const s = Math.min(canvas.width / w, canvas.height / h);
      g.drawImage(src, (canvas.width - w * s) / 2, (canvas.height - h * s) / 2, w * s, h * s);
    };
    if (costume.dataFormat === 'svg' && costume._svg) {
      const img = await rasterizeSvg(costume._svg, 2);
      if (img) fit(img.width, img.height, img.canvas);
      return;
    }
    if (costume._data) {
      const blob = new Blob([costume._data]);
      const url = URL.createObjectURL(blob);
      const im = new Image();
      im.onload = () => fit(im.naturalWidth, im.naturalHeight, im);
      im.src = url;
    }
  }

  renderSpriteInfo() {
    const t = this.current;
    const box = document.getElementById('sprite-info');
    box.classList.toggle('stage-only', !!t.isStage);
    const nameLabel = box.querySelector('.name-field');
    if (nameLabel && nameLabel.childNodes[0]) nameLabel.childNodes[0].textContent = t.isStage ? 'Stage ' : 'Sprite ';
    document.getElementById('sprite-name').value = t.name;
    document.getElementById('sprite-name').disabled = !!t.isStage;
    document.getElementById('sprite-x').value = Math.round(t.x || 0);
    document.getElementById('sprite-y').value = Math.round(t.y || 0);
    document.getElementById('sprite-size').value = t.size || 100;
    document.getElementById('sprite-dir').value = t.direction || 90;
    document.getElementById('sprite-show').checked = t.visible !== false;
    document.getElementById('sprite-rot').value = t.rotationStyle || 'all around';
  }

  renderCostumeList() {
    const el = document.getElementById('costume-list');
    el.innerHTML = '';
    this.current.costumes.forEach((c, i) => {
      const card = document.createElement('div');
      card.className = 'asset-card' + (i === this.current.currentCostume ? ' selected' : '');
      const cv = document.createElement('canvas');
      cv.width = 100; cv.height = 72;
      this.drawThumb(cv, c);
      const nm = document.createElement('div');
      nm.className = 'nm';
      nm.textContent = c.name;
      card.append(cv, nm);
      if (this.current.costumes.length > 1) {
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'asset-del';
        del.dataset.tip = 'Delete';
        del.setAttribute('aria-label', 'Delete');
        del.innerHTML = ICONS.close;
        del.onclick = (e) => {
          e.stopPropagation();
          this.deleteCostume(i);
        };
        card.appendChild(del);
      }
      card.onclick = () => {
        this.current.currentCostume = i;
        this.paint.loadCostume();
        this.renderCostumeList();
        this.renderSprites();
        this.player.draw();
      };
      card.oncontextmenu = (e) => {
        e.preventDefault();
        this.deleteCostume(i);
      };
      el.appendChild(card);
    });
  }

  deleteCostume(i) {
    if (this.current.costumes.length <= 1) return;
    this.current.costumes.splice(i, 1);
    if (this.current.currentCostume > i) this.current.currentCostume -= 1;
    if (this.current.currentCostume >= this.current.costumes.length) {
      this.current.currentCostume = this.current.costumes.length - 1;
    }
    this.paint.loadCostume();
    this.renderCostumeList();
    this.renderSprites();
    this.player.draw();
  }

  renderSoundList() {
    const el = document.getElementById('sound-list');
    el.innerHTML = '';
    this.current.sounds.forEach((s, i) => {
      const card = document.createElement('div');
      card.className = 'asset-card' + (i === (this.current._soundIndex || 0) ? ' selected' : '');
      const nm = document.createElement('div');
      nm.className = 'nm';
      nm.textContent = s.name;
      card.appendChild(nm);
      card.onclick = () => {
        this.current._soundIndex = i;
        this.sounds.loadSound();
        this.renderSoundList();
      };
      card.oncontextmenu = (e) => {
        e.preventDefault();
        if (confirm('Delete sound?')) {
          this.current.sounds.splice(i, 1);
          this.current._soundIndex = 0;
          this.sounds.loadSound();
          this.renderSoundList();
        }
      };
      el.appendChild(card);
    });
  }

  renderMonitors(mons) {
    const layer = document.getElementById('monitor-layer');
    layer.innerHTML = '';
    for (const m of mons || []) {
      const d = document.createElement('div');
      d.className = 'monitor';
      d.style.left = (m.x || 8) + 'px';
      d.style.top = (m.y || 8) + 'px';
      d.innerHTML = `${m.params.VARIABLE || m.params.LIST}<span class="val">${m.value}</span>`;
      layer.appendChild(d);
    }
  }

  selectTarget(t) {
    this.current.scriptText = document.getElementById('code-input').value;
    this.current = t;
    this.updateCostumeTabLabel();
    this.renderAll();
    if (this.tab === 'costumes') this.paint.loadCostume();
    if (this.tab === 'sounds') this.sounds.loadSound();
    this.player.draw();
  }

  updateCostumeTabLabel() {
    const stage = !!this.current?.isStage;
    document.getElementById('tab-costumes-label').textContent = stage ? 'Backdrops' : 'Costumes';
    document.getElementById('tab-costumes-icon').innerHTML = stage ? ICONS.backdrops : ICONS.costumes;
    document.getElementById('costume-list-title').textContent = stage ? 'Backdrops' : 'Costumes';
  }

  openBackdrops() {
    this.selectTarget(this.project.targets[0]);
    this.setTab('costumes');
  }

  setTab(tab) {
    this.tab = tab;
    document.querySelectorAll('.tab').forEach((b) => b.classList.toggle('active', b.dataset.tab === tab));
    document.getElementById('tab-code').classList.toggle('hidden', tab !== 'code');
    document.getElementById('tab-costumes').classList.toggle('hidden', tab !== 'costumes');
    document.getElementById('tab-sounds').classList.toggle('hidden', tab !== 'sounds');
    this.updateCostumeTabLabel();
    if (tab === 'costumes') { this.renderCostumeList(); this.paint.loadCostume(); }
    if (tab === 'sounds') { this.renderSoundList(); this.sounds.loadSound(); }
  }

  addSprite(surprise) {
    const names = this.project.targets.map((t) => t.name);
    const s = makeSprite(uniqueName('Sprite', names));
    if (surprise) s.costumes = [surpriseCostume()];
    s.layerOrder = this.project.targets.length;
    s.scriptText = 'when flag_clicked():\n    \n';
    this.project.targets.push(s);
    this.selectTarget(s);
    this.player.preloadImages();
  }

  deleteSprite(t) {
    this.project.targets = this.project.targets.filter((s) => s !== t);
    this.selectTarget(this.project.targets[1] || this.project.targets[0]);
  }

  addCostume(c) {
    this.current.costumes.push(c);
    this.current.currentCostume = this.current.costumes.length - 1;
    this.renderCostumeList();
    this.paint.loadCostume();
    this.player.preloadImages();
  }

  async uploadCostume(file) {
    if (!file) return;
    const buf = new Uint8Array(await file.arrayBuffer());
    const name = file.name.replace(/\.[^.]+$/, '');
    if (file.type.includes('svg') || file.name.endsWith('.svg')) {
      const fitted = fitSvgToArtboard(new TextDecoder().decode(buf));
      const c = paintedCostume(name, fitted.svg);
      c.rotationCenterX = fitted.cx;
      c.rotationCenterY = fitted.cy;
      this.addCostume(c);
    } else {
      const { paintedBitmapCostume } = await import('./sb3.js');
      const img = new Image();
      img.onload = async () => {
        const fitted = await fitBitmapToArtboard(img);
        this.addCostume(paintedBitmapCostume(name, fitted.data, fitted.w, fitted.h));
      };
      img.src = URL.createObjectURL(file);
    }
  }

  async uploadSound(file) {
    if (!file) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const buf = await ctx.decodeAudioData(await file.arrayBuffer());
    const samples = buf.getChannelData(0);
    const { encodeWav } = await import('./assets.js');
    const { soundFromWav } = await import('./sb3.js');
    const wav = encodeWav(samples, buf.sampleRate);
    this.current.sounds.push(soundFromWav(file.name.replace(/\.[^.]+$/, ''), wav, buf.sampleRate));
    this.current._soundIndex = this.current.sounds.length - 1;
    this.renderSoundList();
    this.sounds.loadSound();
  }

  makeVar(isList) {
    const name = prompt(isList ? 'List name' : 'Variable name', isList ? 'items' : 'score');
    if (!name) return;
    const local = !this.current.isStage && confirm('For this sprite only? Cancel = for all sprites');
    addVariable(this.project, this.current, name, local, isList);
    this.renderBlocks();
    if (!isList) this.insert(`set(${name}, 0)`);
  }

  makeBlock() {
    const name = prompt('Block name (function style)', 'dance');
    if (!name) return;
    const args = prompt('Argument names, comma separated', 'steps');
    const argList = (args || '').split(',').map((s) => s.trim()).filter(Boolean);
    this.insert(`define ${name}(${argList.join(', ')}):`);
  }

  async run() {
    this.current.scriptText = document.getElementById('code-input').value;
    try {
      compileProject(this.project);
      document.getElementById('parse-status').textContent = 'Running on TurboWarp VM';
      document.getElementById('parse-status').className = 'parse-ok';
    } catch (e) {
      document.getElementById('parse-status').textContent = e.message;
      document.getElementById('parse-status').className = 'parse-err';
      return;
    }
    await this.player.load(this.project);
    this.player.greenFlag();
    document.getElementById('engine-label').textContent = this.player.engineName || 'Scratch 3';
  }

  async recompile() {
    this.current.scriptText = document.getElementById('code-input').value;
    try {
      compileProject(this.project);
      document.getElementById('parse-status').textContent = 'Recompiled';
      document.getElementById('parse-status').className = 'parse-ok';
    } catch (e) {
      document.getElementById('parse-status').textContent = e.message;
      document.getElementById('parse-status').className = 'parse-err';
      return;
    }
    await this.player.recompile(this.project);
    document.getElementById('engine-label').textContent = this.player.engineName || 'Scratch 3';
  }

  toggleTurbo() {
    this.player.turbo = !this.player.turbo;
    document.getElementById('turbo-check').checked = this.player.turbo;
  }

  newProject() {
    if (!confirm('Start a new project? Unsaved changes will be lost.')) return;
    this.project = defaultProject();
    this.current = this.project.targets[1];
    this.player.load(this.project);
    this.renderAll();
  }

  async openFile(file) {
    if (!file) return;
    try {
      this.project = await loadSb3(await file.arrayBuffer());
      this.project._name = file.name.replace(/\.sb3$/i, '');
      this.current = this.project.targets.find((t) => !t.isStage) || this.project.targets[0];
      await this.player.load(this.project);
      this.renderAll();
    } catch (e) {
      alert('Could not open project: ' + e.message);
    }
  }

  async save() {
    this.current.scriptText = document.getElementById('code-input').value;
    try { compileProject(this.project); } catch (e) {
      if (!confirm(e.message + '\nSave anyway?')) return;
    }
    const data = await saveSb3(this.project);
    const blob = new Blob([data], { type: 'application/zip' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = (this.project._name || 'Untitled') + '.sb3';
    a.click();
  }

  async saveToBrowser(asNew) {
    this.current.scriptText = document.getElementById('code-input').value;
    this.project._name = document.getElementById('project-name').value || this.project._name || 'Untitled';
    try { compileProject(this.project); } catch (e) {
      if (!confirm(e.message + '\nSave anyway?')) return;
    }
    try {
      const data = await saveSb3(this.project);
      const id = asNew || !this.project._saveId ? newSaveId() : this.project._saveId;
      writeSave(id, this.project._name, data);
      this.project._saveId = id;
    } catch (e) {
      alert('Could not save to this browser (storage full or blocked).');
      return;
    }
  }

  showSavedProjects() {
    const modal = document.getElementById('saves-modal');
    const list = document.getElementById('saves-list');
    const saves = listSaves();
    list.innerHTML = '';
    if (!saves.length) {
      const empty = document.createElement('div');
      empty.className = 'saves-empty';
      empty.textContent = 'No saved projects';
      list.appendChild(empty);
    } else {
      for (const rec of saves) {
        const row = document.createElement('div');
        row.className = 'save-row' + (rec.id === this.project._saveId ? ' current' : '');
        const info = document.createElement('div');
        info.className = 'save-row-info';
        const name = document.createElement('div');
        name.className = 'save-row-name';
        name.textContent = rec.name || 'Untitled';
        const date = document.createElement('div');
        date.className = 'save-row-date';
        date.textContent = rec.savedAt ? new Date(rec.savedAt).toLocaleString() : '';
        info.append(name, date);
        const load = document.createElement('button');
        load.type = 'button';
        load.textContent = 'Load';
        load.onclick = () => this.loadFromBrowser(rec.id);
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'save-del';
        del.textContent = 'Delete';
        del.onclick = () => {
          deleteSave(rec.id);
          if (this.project._saveId === rec.id) this.project._saveId = null;
          this.showSavedProjects();
        };
        row.append(info, load, del);
        list.appendChild(row);
      }
    }
    modal.classList.remove('hidden');
  }

  hideSavedProjects() {
    document.getElementById('saves-modal').classList.add('hidden');
  }

  async loadFromBrowser(id) {
    const data = readSave(id);
    if (!data) {
      alert('That save is missing.');
      this.showSavedProjects();
      return;
    }
    try {
      this.project = await loadSb3(data);
      const rec = listSaves().find((s) => s.id === id);
      this.project._name = rec?.name || this.project._name || 'Untitled';
      this.project._saveId = id;
      this.current = this.project.targets.find((t) => !t.isStage) || this.project.targets[0];
      await this.player.load(this.project);
      this.renderAll();
      this.hideSavedProjects();
    } catch (e) {
      alert('Could not open save: ' + e.message);
    }
  }

  exportTxt() {
    const blob = new Blob([this.current.scriptText || ''], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = this.current.name + '.txt';
    a.click();
  }
}

function isOpaqueColor(bg) {
  if (!bg || bg === 'transparent') return false;
  const m = String(bg).match(/rgba?\(([^)]+)\)/i);
  if (!m) return true;
  const parts = m[1].split(',').map((s) => Number(s.trim()));
  if (parts.length >= 4 && parts[3] === 0) return false;
  return true;
}

window.addEventListener('DOMContentLoaded', () => {
  window.app = new App();
});
